import { createJsonNormalizer, createShapeNormalizer } from '@change-corgi/fcm/normalizers';
import type { Normalizer } from '@change-corgi/fcm/normalizers';

// Salt is intentionally not part of this config's shape - PR feedback on the POC (change/corgi#25792)
// concluded it doesn't need to be admin-editable; callers hardcode it when invoking the picker.
const FCM_EXCLUSION_GROUP_CONFIG_DEFAULT_VALUE = {
	members: [],
};

const normalizer = createShapeNormalizer({
	type: 'object',
	shape: {
		members: {
			type: 'array',
			shape: {
				type: 'object',
				shape: {
					experimentId: { type: 'string' },
					weight: { type: 'number' },
				},
			},
		},
	},
});

// Weight sums can pick up floating-point drift (e.g. 0.1 + 0.2 + 0.7 > 1), so the "sums to more than 1"
// check needs slack, not exact equality.
const WEIGHT_SUM_TOLERANCE = 1e-9;

const normalizeAndValidateMembers: Normalizer<ReturnType<typeof normalizer>> = (value: unknown) => {
	const res = normalizer(value);
	const experimentIds = res.members.map(({ experimentId }) => experimentId);
	if (new Set(experimentIds).size !== experimentIds.length) {
		throw new Error('duplicate experimentId in exclusion group members');
	}
	// the shape normalizer only checks `typeof weight === 'number'`, which lets NaN/Infinity/out-of-range
	// values through (e.g. an oversized JSON number literal overflows to Infinity on parse).
	if (res.members.some(({ weight }) => !Number.isFinite(weight) || weight < 0 || weight > 1)) {
		throw new Error('exclusion group member weight must be a finite number between 0 and 1');
	}
	if (res.members.reduce((acc, { weight }) => acc + weight, 0) > 1 + WEIGHT_SUM_TOLERANCE) {
		throw new Error('exclusion group member weights sum to more than 1');
	}
	return res;
};

export type FcmExclusionGroupConfigValueType = ReturnType<typeof normalizer>;

export const createExclusionGroupFcmConfig = (
	name: string,
): {
	name: string;
	normalizer: Normalizer<FcmExclusionGroupConfigValueType>;
	defaultValue: FcmExclusionGroupConfigValueType;
} => ({
	name,
	normalizer: createJsonNormalizer(normalizeAndValidateMembers),
	defaultValue: FCM_EXCLUSION_GROUP_CONFIG_DEFAULT_VALUE,
});
